// src/components/Map/MarkerManager/Marker.tsx

import React, {
  memo,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { ROUTES } from "../../../config/routes";
import { Marker, Tooltip } from "react-leaflet";
import { Icon, LeafletMouseEvent, Tooltip as LTooltip } from "leaflet";
import MapIconPin from "../../../assets/icons/MapIconPin.svg";
import MapIconPinWarning from "../../../assets/icons/MapIconPinWarning.svg";
import ArchiveBoxIcon from "@heroicons/react/24/outline/ArchiveBoxIcon";
import { ReactComponent as MosquitoIconSvg } from "../../../assets/icons/MosquitoVector.svg";

import { Typography, Badge } from "antd";
import { MarkerData } from "../../../types/components/map";
import { useTrapsiteEditorContext } from "../TrapsiteEditor/TrapsiteEditorContext";

const { Text } = Typography;

// Every pin is interactive now - a collection location opens its page, a trap
// offers its own menu on right-click - so none of them get the map's grab
// cursor, which read as "this is scenery, drag the map".
const makeTrapIcon = (warning: boolean) =>
  new Icon({
    iconUrl: warning ? MapIconPinWarning : MapIconPin,
    iconAnchor: [15, 40],
    className: "w-10 h-10 translate-y-[20px] z-1000 !cursor-pointer",
  });

const TrapMarker = memo(({ marker }: { marker: MarkerData }) => {
  const navigate = useNavigate();
  const editor = useTrapsiteEditorContext();
  const tooltipRef = useRef<LTooltip>(null);
  const [direction, setDirection] = useState<
    "auto" | "top" | "bottom" | "left" | "right"
  >("auto");

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (direction === "auto") {
      timeoutId = setTimeout(() => {
        const el = tooltipRef.current?.getElement();
        if (el) {
          const match = el.className.match(
            /leaflet-tooltip-(top|bottom|left|right)/,
          );
          if (match)
            setDirection(match[1] as "top" | "bottom" | "left" | "right");
        }
      }, 50);
    }

    const tooltip = tooltipRef.current;
    if (!tooltip) return;
    tooltip.options.direction = direction;
    const el = tooltip.getElement();
    if (el) {
      el.classList.remove("leaflet-tooltip-left", "leaflet-tooltip-right");
      el.classList.add(`leaflet-tooltip-${direction}`);
      // The label has `pointer-events: auto`, but a tooltip is a
      // non-interactive layer, so Leaflet never treats it as an event target -
      // a right-click on it reaches the map with the LABEL's latlng, not the
      // pin's. Stamping the id lets MapContextMenuController recover which
      // site was actually clicked. See the note there.
      if (marker.id) el.dataset.trapsiteId = marker.id;
      if (marker.deviceUuid) el.dataset.deviceUuid = marker.deviceUuid;
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [direction, marker.popupOpen, marker.id, marker.deviceUuid]);

  const isClickable = marker.markerType !== "device";

  const icon = useMemo(() => makeTrapIcon(!!marker.warning), [marker.warning]);

  const handleNavigate = useCallback(() => {
    if (marker?.id) {
      navigate(generatePath(ROUTES.location_detail, { id: marker.id }));
    }
  }, [marker?.id, navigate]);

  // A marker is either a collection location or a trap, and each offers its
  // own menu.
  const menuTarget = useMemo(() => {
    if (marker.deviceUuid) {
      return { kind: "device" as const, uuid: marker.deviceUuid };
    }
    if (marker.id) return { kind: "trapsite" as const, id: marker.id };
    return null;
  }, [marker.deviceUuid, marker.id]);

  const eventHandlers = useMemo(
    () => ({
      ...(isClickable ? { click: handleNavigate } : {}),
      ...(editor.enabled && menuTarget
        ? {
            contextmenu: (e: LeafletMouseEvent) =>
              // Viewport coordinates, not e.containerPoint: Leaflet reports a
              // marker's container point as the PIN's position rather than the
              // cursor's, so the menu would jump to the pin.
              editor.openMenu(
                { x: e.originalEvent.clientX, y: e.originalEvent.clientY },
                menuTarget,
              ),
          }
        : {}),
    }),
    [isClickable, handleNavigate, editor, menuTarget],
  );

  return (
    <Marker
      position={marker.position}
      zIndexOffset={700}
      icon={icon}
      // Leaflet collects [marker, map] as event targets and fires both, and it
      // hands the map the MARKER's latlng - so without this a right-click on a
      // pin would show "Edit" and have it instantly replaced by "Create" at
      // that pin's own coordinate. contextmenu is in Leaflet's _mouseEvents,
      // so setting this stops the fire loop at the marker.
      bubblingMouseEvents={false}
      eventHandlers={eventHandlers}
    >
      {marker.popupOpen && (
        <Tooltip
          ref={tooltipRef}
          key={`${marker?.warning}`}
          permanent
          className={`map-marker ${marker?.warning ? "warning" : ""}`}
          direction={direction}
        >
          <div
            className={`px-2 py-2 flex items-center min-w-[12rem] gap-3 pr-6 marker-content ${isClickable ? "clickable" : ""}`}
            onClick={isClickable ? handleNavigate : undefined}
          >
            <div className="flex flex-col flex-grow justify-center">
              <div className="flex items-center gap-2">
                {marker.markerType === "device" && (
                  <ArchiveBoxIcon className="w-5 h-5 flex-shrink-0 trapIcon" />
                )}
                <Text className="marker-display-name m-0">
                  {marker.displayName}
                </Text>
              </div>
              {marker.markerType === "device" && marker.gps && (
                <Text className="marker-gps-text m-0 mt-1 pl-7">
                  {marker.gps.lat.toFixed(4)}, {marker.gps.lon.toFixed(4)}
                </Text>
              )}
            </div>

            <div className="flex items-center gap-2">
              {marker.maintainCount ? (
                <Badge
                  count={marker.maintainCount}
                  offset={[0, 2]}
                  color="#f4c000"
                >
                  <ArchiveBoxIcon className="w-5 h-5 trapIcon warning" />
                </Badge>
              ) : null}
              {marker.detectionsCount ? (
                <Badge
                  count={marker.detectionsCount}
                  color="#f4c000"
                  offset={[0, 2]}
                >
                  <MosquitoIconSvg
                    width={20}
                    height={20}
                    fill="#f4c000"
                    className="trapIcon warning"
                  />
                </Badge>
              ) : null}
            </div>
          </div>
        </Tooltip>
      )}
    </Marker>
  );
});

export default TrapMarker;
